import { useState, useEffect } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Upload, FileText, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function BulkVacancyUpload() {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dryRun, setDryRun] = useState(true);
  const [result, setResult] = useState(null);
  const [template, setTemplate] = useState(null);

  useEffect(() => {
    api.get("/admin/vacancies/bulk-upload/template").then((r) => setTemplate(r.data)).catch(() => {});
  }, []);

  const submit = async () => {
    if (!file) { toast.error("Choose a .docx file first"); return; }
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/admin/vacancies/bulk-upload?dry_run=${dryRun}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(r.data);
      toast.success(`${dryRun ? "Dry-run: " : ""}${r.data.created} created${r.data.skipped ? `, ${r.data.skipped} skipped` : ""}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6" data-testid="admin-bulk-vacancy-upload">
      <div className="flex items-center gap-2">
        <Upload className="w-4 h-4 text-sky" />
        <div className="font-display font-bold text-ink text-lg">Bulk vacancy upload (.docx)</div>
      </div>
      <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
        Faster than posting single vacancies one by one. Upload a Word doc with one vacancy per paragraph in the format:{" "}
        <code className="font-mono text-xs bg-paper px-1 rounded">Title: … | URL: … | Employer: … | Location: … | Salary: 30000-42000 | Expiry: 2026-12-31 | Role: train-driver</code>.
        Title and URL are required; other fields optional. Employer is best-effort matched by name or by URL domain.
      </p>

      {template && (
        <details className="mt-4 text-xs bg-paper border border-border rounded p-3" data-testid="bulk-template-help">
          <summary className="cursor-pointer font-semibold text-ink">Show format reference</summary>
          <div className="mt-2 text-muted-foreground">
            <div><span className="font-semibold">Required:</span> {template.required_fields.join(", ")}</div>
            <div><span className="font-semibold">Optional:</span> {template.optional_fields.join(", ")}</div>
            <div><span className="font-semibold">Roles:</span> {template.valid_role_categories.join(", ")}</div>
            <div><span className="font-semibold">Types:</span> {template.valid_employment_types.join(", ")}</div>
            <pre className="mt-2 whitespace-pre-wrap font-mono bg-white border border-border rounded p-2 text-[11px]">{template.example_body}</pre>
          </div>
        </details>
      )}

      <div className="mt-4 flex flex-col sm:flex-row gap-3 sm:items-end">
        <label className="flex-1">
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-1">Word document</div>
          <input
            type="file"
            accept=".docx"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="block w-full text-sm text-ink file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-navy file:text-bone file:font-semibold hover:file:bg-navy/90"
            data-testid="bulk-file-input"
          />
          {file && <div className="mt-1 text-[11px] text-muted-foreground flex items-center gap-1"><FileText className="w-3 h-3" /> {file.name}</div>}
        </label>
        <label className="flex items-center gap-2 text-sm text-ink select-none">
          <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} data-testid="bulk-dry-run" />
          Dry-run (parse only, no DB writes)
        </label>
        <Button onClick={submit} disabled={busy || !file} className="bg-navy text-bone hover:bg-lime-600 hover:text-ink font-semibold" data-testid="bulk-upload-submit">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
          {dryRun ? "Preview" : "Upload & create"}
        </Button>
      </div>

      {result && (
        <div className="mt-5 pt-5 border-t border-border" data-testid="bulk-upload-result">
          <div className="text-sm">
            <span className="text-muted-foreground">File:</span> <span className="font-mono">{result.file}</span> ·{" "}
            <span className="text-muted-foreground">Lines:</span> {result.total_lines} ·{" "}
            <span className="text-emerald-800 font-semibold">Created: {result.created}</span> ·{" "}
            {result.skipped > 0 && <span className="text-rose-700 font-semibold">Skipped: {result.skipped}</span>}
            {result.dry_run && <span className="ml-2 text-[10px] uppercase tracking-wider bg-amber-100 text-amber-900 px-1.5 py-0.5 rounded">Dry-run</span>}
          </div>
          <div className="mt-3 max-h-[360px] overflow-auto">
            <table className="w-full text-xs">
              <thead className="bg-sand text-ink">
                <tr className="text-left">
                  <th className="px-2 py-1.5 font-semibold">#</th>
                  <th className="px-2 py-1.5 font-semibold">Status</th>
                  <th className="px-2 py-1.5 font-semibold">Title</th>
                  <th className="px-2 py-1.5 font-semibold">Employer</th>
                  <th className="px-2 py-1.5 font-semibold">Match</th>
                  <th className="px-2 py-1.5 font-semibold">Detail</th>
                </tr>
              </thead>
              <tbody>
                {(result.rows || []).map((r) => {
                  const bad = r.status === "skipped";
                  return (
                    <tr key={r.line_number} className={`border-t border-border align-top ${bad ? "bg-rose-50" : ""}`}>
                      <td className="px-2 py-1.5 font-mono">{r.line_number}</td>
                      <td className="px-2 py-1.5">{bad ? <span className="text-rose-800 font-semibold flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{r.status}</span> : <span className="text-emerald-800 font-semibold flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />{r.status}</span>}</td>
                      <td className="px-2 py-1.5 text-ink">{r.title || "—"}</td>
                      <td className="px-2 py-1.5 text-muted-foreground">{r.employer_name || "—"}</td>
                      <td className="px-2 py-1.5">{r.employer_matched ? <span className="text-emerald-700">domain</span> : r.status === "skipped" ? "—" : <span className="text-amber-700">fallback</span>}</td>
                      <td className="px-2 py-1.5 text-muted-foreground text-[11px] font-mono break-all">{r.reason || r.role_category || ""}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
